import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { merge, of, switchMap } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { AutomationStatus } from '../../../models/requirement.model';

interface StatusOption {
  value: AutomationStatus;
  modifier: string;
  labelKey: string;
}

const FRAMEWORK_OPTIONS = [
  { value: 'SELENIUM', label: 'Selenium' },
  { value: 'CYPRESS', label: 'Cypress' },
  { value: 'PLAYWRIGHT', label: 'Playwright' },
  { value: 'JUNIT', label: 'JUnit' },
  { value: 'TESTNG', label: 'TestNG' },
  { value: 'APPIUM', label: 'Appium' },
  { value: 'ROBOT_FRAMEWORK', label: 'Robot Framework' },
  { value: 'PYTEST', label: 'Pytest' },
  { value: 'OTHER', label: 'Sonstige' },
] as const;

@Component({
  selector: 'app-requirement-automation-card',
  imports: [TranslatePipe, MatIcon, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption],
  templateUrl: './requirement-automation-card.component.html',
  styleUrl: './requirement-automation-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementAutomationCardComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly statusControl = input.required<FormControl<AutomationStatus>>();
  readonly frameworkControl = input.required<FormControl<string | null>>();
  readonly automationKey = input<string | null>(null);
  readonly readOnly = input(false);

  readonly status = toSignal(
    toObservable(this.statusControl).pipe(switchMap((control) => merge(of(control.value), control.valueChanges))),
    { initialValue: 'MANUAL' as AutomationStatus },
  );
  readonly copied = signal(false);

  private copyResetHandle: ReturnType<typeof setTimeout> | null = null;

  readonly frameworkOptions = FRAMEWORK_OPTIONS;

  readonly statusOptions: readonly StatusOption[] = [
    { value: 'MANUAL', modifier: 'manual', labelKey: 'requirements.form.automation.status.manual' },
    { value: 'AUTOMATABLE', modifier: 'automatable', labelKey: 'requirements.form.automation.status.automatable' },
    { value: 'AUTOMATED', modifier: 'automated', labelKey: 'requirements.form.automation.status.automated' },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => this.clearCopyReset());
  }

  selectStatus(value: AutomationStatus): void {
    if (this.readOnly()) {
      return;
    }
    const control = this.statusControl();
    if (control.value === value) {
      return;
    }
    control.setValue(value);
    control.markAsDirty();
  }

  copyKey(): void {
    const key = this.automationKey();
    if (!key || !navigator.clipboard) {
      return;
    }
    navigator.clipboard.writeText(key).then(
      () => this.flagCopied(),
      () => {},
    );
  }

  private flagCopied(): void {
    this.copied.set(true);
    this.clearCopyReset();
    this.copyResetHandle = setTimeout(() => this.copied.set(false), 1400);
  }

  private clearCopyReset(): void {
    if (this.copyResetHandle !== null) {
      clearTimeout(this.copyResetHandle);
      this.copyResetHandle = null;
    }
  }
}
